import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Input, message, Modal, Popconfirm, Radio, Row, Select, Table, Upload } from "antd";
import { EditOutlined, PlusCircleFilled, PlusOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import ApiClient from "../../helpers/ApiClient";
import UpdatedComponent from "../ExportExcel";
import excel from "../assets/vector-icons/excel.png";
import search from "../assets/vector-icons/search.png";
import exchange from "../assets/vector-icons/exchange.png";

const BASE = process.env.REACT_APP_BASE_URL;
const uploadActionUrl = BASE + "media/upload/multipleImages";

// Helper function to safely construct image URLs
const getImageUrl = (imagePath) => {
  if (!imagePath) return "";
  // If already a full URL, return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  // Remove leading slash if present
  const cleanPath = imagePath.startsWith("/") ? imagePath.substring(1) : imagePath;
  // Ensure BASE doesn't have trailing slash and path doesn't have leading slash
  const baseUrl = BASE.endsWith("/") ? BASE.slice(0, -1) : BASE;
  return `${baseUrl}/${cleanPath}`;
};

const HomeSlider = (props) => {
  const { Option } = Select;
  const [modalVisible, setModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sliderId, setSliderId] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [showRowCount, setShowRowCount] = useState(false);
  const [imageNames, setImageNames] = useState({});
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    getSliders();
  }, []);

  useEffect(() => {
  if (dataSource.length) {
    setShowRowCount(true);
    const timer = setTimeout(() => setShowRowCount(false), 3000);
    return () => clearTimeout(timer);
  }
}, [dataSource]);


  const getSliders = () => {
    ApiClient.get("admin/homeSliders")
      .then((res) => {
        if (res && res.status == 200 && res.data && Array.isArray(res.data)) {
          const data = res.data.map((item, index) => ({
            ...item,
            Sno: index + 1,
            CategoryName: getCategoryName(item.Category),
            DirectionName: item.Direction === 1 ? "Left to Right" : "Right to Left",
          }));
          setDataSource(data);
        } else {
          console.error("Invalid response structure:", res);
        }
      })
      .catch((err) => {
        console.error("Error fetching sliders:", err);
      });
  };

  const getCategoryName = (id) => {
    const categories = { 1: "Flights", 2: "Hotels", 3: "Tours", 4: "Activities", 5: "Transfers" };
    return categories[id] || "";
  };

  const resetModalState = () => {
    setIsEdit(false);
    setSliderId(null);
    form.resetFields();
    setFileList([]);
    setImageNames({});
    setImageUrls({});
  };

  const showModal = () => {
    resetModalState();
    setModalVisible(true);
  };

  const handleModalClose = () => {
    resetModalState();
    setModalVisible(false);
  };

  const editSlider = (record) => {
    // First reset all state to ensure clean slate
    resetModalState();
    
    // Now set edit mode and populate with record data
    setIsEdit(true);
    setSliderId(record.SliderID);
    
    const images = [record.Image1, record.Image2, record.Image3, record.Image4, record.Image5]
      .filter(Boolean)
      .map((img, idx) => ({
        uid: idx + 1,
        name: "image.jpg",
        status: "done",
        url: getImageUrl(img),
        response: { status: 200, data: { filepaths: [img] } },
      }));
    setFileList(images);
    
    // Set image names and URLs - explicitly set to empty string if not present
    const names = {};
    const urls = {};
    names.Image1Name = record.Image1Name || "";
    urls.Image1Url = record.Image1Url || "";
    names.Image2Name = record.Image2Name || "";
    urls.Image2Url = record.Image2Url || "";
    names.Image3Name = record.Image3Name || "";
    urls.Image3Url = record.Image3Url || "";
    names.Image4Name = record.Image4Name || "";
    urls.Image4Url = record.Image4Url || "";
    names.Image5Name = record.Image5Name || "";
    urls.Image5Url = record.Image5Url || "";
    
    setImageNames(names);
    setImageUrls(urls);
    
    // Reset form first, then set values
    form.resetFields();
    form.setFieldsValue({
      Category: record.Category,
      Title: record.Title,
      Direction: record.Direction,
      Status: record.Status,
      Image1Name: record.Image1Name || "",
      Image1Url: record.Image1Url || "",
      Image2Name: record.Image2Name || "",
      Image2Url: record.Image2Url || "",
      Image3Name: record.Image3Name || "",
      Image3Url: record.Image3Url || "",
      Image4Name: record.Image4Name || "",
      Image4Url: record.Image4Url || "",
      Image5Name: record.Image5Name || "",
      Image5Url: record.Image5Url || "",
    });
    setModalVisible(true);
  };

  const deleteSlider = (id) => {
    ApiClient.delete("admin/homeSlider/" + id)
      .then((res) => {
        if (res.status == 200) {
          message.success("Slider deleted successfully");
          getSliders();
        }
      })
      .catch(() => {});
  };

  const hasActiveSliderForCategory = (category, excludeId = null) => {
  return dataSource.some(
    (item) =>
      item.Category === category &&
      item.Status === 0 &&
      item.SliderID !== excludeId
  );
};

  const handleChange = ({ fileList }) => setFileList(fileList);

  const submitForm = (values) => {
    if (fileList.length < 4) {
      message.error("Please upload at least 4 images");
      return;
    }
if (values.Status === 0) {
    const exists = hasActiveSliderForCategory(
      values.Category,
      isEdit ? sliderId : null
    );

    if (exists) {
      message.error("Only one ACTIVE slider is allowed per category");
      return;
    }
  }
    const images = fileList.map((file) => file.response?.data?.filepaths?.[0] || "");
    const formData = {
      Category: values.Category,
      Title: values.Title,
      Direction: values.Direction,
      Image1: images[0] || "",
      Image1Name: values.Image1Name || "",
      Image1Url: values.Image1Url || "",
      Image2: images[1] || "",
      Image2Name: values.Image2Name || "",
      Image2Url: values.Image2Url || "",
      Image3: images[2] || "",
      Image3Name: values.Image3Name || "",
      Image3Url: values.Image3Url || "",
      Image4: images[3] || "",
      Image4Name: values.Image4Name || "",
      Image4Url: values.Image4Url || "",
      Image5: images[4] || "",
      Image5Name: values.Image5Name || "",
      Image5Url: values.Image5Url || "",
      Status: values.Status,
    };

    const apiCall = isEdit
      ? ApiClient.put("admin/homeSlider/" + sliderId, {}, formData)
      : ApiClient.post("admin/homeSlider", formData);

    apiCall
      .then((res) => {
        if (res && res.status == 200) {
          message.success(isEdit ? "Updated successfully" : "Added successfully");
          handleModalClose();
          getSliders();
        } else {
          message.error(res?.message || (isEdit ? "Failed to update slider" : "Failed to add slider"));
        }
      })
      .catch((err) => {
        console.error("Error saving slider:", err);
        message.error(err?.message || (isEdit ? "Failed to update slider" : "Failed to add slider"));
      });
  };

  const tableActions = (rec) => (
    <div style={{ display: "flex", justifyContent: "space-evenly" }}>
      <EditOutlined onClick={() => editSlider(rec)} style={{ color: "#1890ff", fontSize: "20px" }} />
      <Popconfirm title="Are you sure to delete?" onConfirm={() => deleteSlider(rec.SliderID)}>
        <DeleteOutlined style={{ color: "red", fontSize: "20px" }} />
      </Popconfirm>
    </div>
  );

  const columns = [
    { title: "S.No.", dataIndex: "Sno" },
    { title: "Category", dataIndex: "CategoryName", sorter: (a, b) => a.CategoryName.localeCompare(b.CategoryName) },
    { title: "Title", dataIndex: "Title" },
    { title: "Direction", dataIndex: "DirectionName" },
    { title: "Status", render: (rec) => <b style={{ color: rec.Status === 0 ? "#008000" : "#ff0000" }}>{rec.Status === 0 ? "Active" : "Inactive"}</b> },
    { title: "Actions", render: tableActions },
  ];

  const searchTableData = (e) => {
    const searchVal = e.target.value;
    if (searchVal.length > 0) {
      const searchResults = dataSource.filter((item) =>
        JSON.stringify(item).toLowerCase().includes(searchVal.toLowerCase())
      );
      setDataSource(searchResults);
    } else {
      getSliders();
    }
  };

  const closeSearchInput = <CloseOutlined onClick={() => setShowSearchBox(false)} />;

  return (
    <div className="manage-markup-section">
      <div className="container-fluid">
        <Row>
          <Col span={24}>
            <Card bordered={false}>
              <div className="card-add-heading">
                <div className="rows-count">
                  <h5>Home Page Sliders</h5>
                  {/* <p>{dataSource.length} rows found</p> */}
                  {showRowCount && <p>{dataSource.length} rows found</p>}

                </div>
                <div className="action-images">
                  {showSearchBox && <Input placeholder="Search" onChange={searchTableData} suffix={closeSearchInput} />}
                  &nbsp;&nbsp;
                  <img src={search} alt="search" onClick={() => setShowSearchBox(true)} />
                  <img src={exchange} alt="refresh" onClick={getSliders} />
                  <p className="add-deposit-icon" onClick={showModal}><PlusCircleFilled /></p>
                </div>
              </div>
              <Table bordered dataSource={dataSource} columns={columns} pagination={{ defaultPageSize: 25 }} />
            </Card>
          </Col>
        </Row>
      </div>

      <Modal title={isEdit ? "Update Slider" : "Add Slider"} visible={modalVisible} onCancel={handleModalClose} width={800} footer={[
        <Button key="submit" type="primary" onClick={form.submit}>{isEdit ? "Update" : "Add"}</Button>,
        <Button key="back" onClick={handleModalClose}>Cancel</Button>,
      ]}>
        <Form form={form} layout="vertical" onFinish={submitForm}>
          <Row gutter={16}>
            <Col md={12} xs={24}>
              <Form.Item label="Category" name="Category" rules={[{ required: true }]}>
                <Select placeholder="Select Category" disabled={isEdit}>
                  <Option value={1}>Flights</Option>
                  <Option value={2}>Hotels</Option>
                  <Option value={3}>Bus</Option>
                  <Option value={4}>Activities</Option>
                  <Option value={5}>Mapping</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label="Title" name="Title" rules={[{ required: true }]}>
                <Input placeholder="Enter Title" />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label="Scroll Direction" name="Direction" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value={1}>Left to Right</Radio>
                  <Radio value={2}>Right to Left</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label="Status" name="Status" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value={0}>Active</Radio>
                  <Radio value={1}>Inactive</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col md={24} xs={24}>
              <Form.Item label="Images (Min 4, Max 5)" rules={[{ required: true }]}>
                <Upload 
                  name="image" 
                  action={uploadActionUrl} 
                  listType="picture-card" 
                  fileList={fileList} 
                  onChange={handleChange} 
                  data={{ category: "homeslider" }}
                  accept=".jpg,.jpeg,.png,.svg,.webp"
                  itemRender={(originNode, file, fileList, actions) => {
                    return (
                      <div style={{ position: 'relative' }}>
                        {originNode}
                        {file.status === 'error' && (
                          <div 
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: '#f5f5f5',
                              color: '#999',
                              fontSize: '12px'
                            }}
                          >
                            Image Error
                          </div>
                        )}
                      </div>
                    );
                  }}
                  beforeUpload={(file) => {
                    const isLt9MB = file.size / 1024 / 1024 < 5;
                    if (!isLt9MB) {
                      message.error("Image must be smaller than 9 MB");
                      return Upload.LIST_IGNORE;
                    }
                    const isImage =
                      file.type.startsWith("image/") ||
                      /\.(jpg|jpeg|png|svg|webp)$/i.test(file.name);

                    if (!isImage) {
                      message.error("Only image files (JPG, PNG, SVG, WEBP) are allowed");
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                >
                  {fileList.length >= 5 ? null : <div><PlusOutlined /><div style={{ marginTop: 8 }}>Upload</div></div>}
                </Upload>
              </Form.Item>
            </Col>
            {fileList.length > 0 && fileList.map((file, index) => {
              const imageNum = index + 1;
              return (
                <React.Fragment key={imageNum}>
                  <Col md={12} xs={24}>
                    <Form.Item 
                      label={`Image ${imageNum} Name`} 
                      name={`Image${imageNum}Name`}
                    >
                      <Input placeholder={`Enter name for image ${imageNum}`} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item 
                      label={`Image ${imageNum} URL`} 
                      name={`Image${imageNum}Url`}
                    >
                      <Input placeholder={`Enter URL for image ${imageNum}`} />
                    </Form.Item>
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default UpdatedComponent(HomeSlider);
